/**
 * Jertscript JavaScript Code Generator
 *
 * Transforms the Jertscript AST into valid JavaScript code.
 * Uses the visitor pattern to traverse each node type.
 *
 * "Honestly, TypeScript could use more croissants." 🥐
 */

import {
  Program,
  Statement,
  Expression,
  BinaryExpr,
  UnaryExpr,
  LiteralExpr,
  IdentifierExpr,
  CallExpr,
  AssignmentExpr,
  MemberAssignmentExpr,
  LogicalExpr,
  GroupingExpr,
  MemberExpr,
  ArrayExpr,
  ObjectExpr,
  ThisExpr,
  NewExpr,
  AwaitExpr,
  ExpressionStmt,
  VariableDeclaration,
  FunctionDeclaration,
  IfStatement,
  WhileStatement,
  ForStatement,
  ReturnStatement,
  BlockStatement,
  TryCatchStatement,
  ClassDeclaration,
  ImportDeclaration,
  ExportDeclaration,
  BreakStatement,
  ContinueStatement,
  ThrowStatement,
} from '../parser/ast';
import { TokenType } from '../lexer/tokens';

/**
 * Built-in function mappings from Jertscript to JavaScript
 */
const BUILTIN_FUNCTIONS: Record<string, string> = {
  'dire': 'console.log',
  'crier': 'console.error',
  'demander': 'prompt',
};

/**
 * XION blockchain function mappings from Jertscript to JavaScript
 * These functions are provided by the xionStdlib from jertscript/stdlib/xion
 */
const XION_FUNCTIONS: Record<string, string> = {
  'connecter': 'xionStdlib.connecter',
  'deconnecter': 'xionStdlib.deconnecter',
  'solde': 'xionStdlib.solde',
  'envoyer': 'xionStdlib.envoyer',
  'executer': 'xionStdlib.executer',
  'requete': 'xionStdlib.requete',
  'obtenirPortefeuille': 'xionStdlib.obtenirPortefeuille',
  'estConnecte': 'xionStdlib.estConnecte',
  'initialiserXion': 'xionStdlib.initialiserXion',
};

/**
 * Generator class that transforms a Jertscript AST into JavaScript code
 */
export class Generator {
  private ast: Program;
  private indentLevel: number = 0;
  private indentString: string = '  '; // 2 spaces per indent level
  private usesXion: boolean = false; // Track if XION functions are used

  constructor(ast: Program) {
    this.ast = ast;
  }

  /**
   * Generate JavaScript code from the AST
   * @returns The generated JavaScript code as a string
   */
  generate(): string {
    // Reset XION usage tracking
    this.usesXion = false;

    const statements = this.ast.statements.map(stmt => this.visitStatement(stmt));
    let code = statements.join('\n');

    // Add XION import at the top if XION functions are used
    if (this.usesXion) {
      const xionImport = '// XION blockchain integration\n' +
        'import { xionStdlib } from "jertscript/stdlib/xion";\n\n';
      code = xionImport + code;
    }

    return code;
  }

  /**
   * Get the current indentation string
   */
  private indent(): string {
    return this.indentString.repeat(this.indentLevel);
  }

  /**
   * Increase indentation level
   */
  private increaseIndent(): void {
    this.indentLevel++;
  }

  /**
   * Decrease indentation level
   */
  private decreaseIndent(): void {
    if (this.indentLevel === 0) {
      // This indicates unbalanced braces in the AST - a serious structural error
      throw new Error(
        'Generator error: Unbalanced indent level (attempted to decrease below 0). ' +
        'This indicates a malformed AST with mismatched block structures.'
      );
    }
    this.indentLevel--;
  }

  // ============================================================================
  // Statement Visitors
  // ============================================================================

  /**
   * Visit any statement node and dispatch to the appropriate visitor
   */
  private visitStatement(stmt: Statement): string {
    switch (stmt.type) {
      case 'ExpressionStmt':
        return this.visitExpressionStmt(stmt as ExpressionStmt);
      case 'VariableDeclaration':
        return this.visitVariableDeclaration(stmt as VariableDeclaration);
      case 'FunctionDeclaration':
        return this.visitFunctionDeclaration(stmt as FunctionDeclaration);
      case 'IfStatement':
        return this.visitIfStatement(stmt as IfStatement);
      case 'WhileStatement':
        return this.visitWhileStatement(stmt as WhileStatement);
      case 'ForStatement':
        return this.visitForStatement(stmt as ForStatement);
      case 'ReturnStatement':
        return this.visitReturnStatement(stmt as ReturnStatement);
      case 'BlockStatement':
        return this.visitBlockStatement(stmt as BlockStatement);
      case 'TryCatchStatement':
        return this.visitTryCatchStatement(stmt as TryCatchStatement);
      case 'ClassDeclaration':
        return this.visitClassDeclaration(stmt as ClassDeclaration);
      case 'ImportDeclaration':
        return this.visitImportDeclaration(stmt as ImportDeclaration);
      case 'ExportDeclaration':
        return this.visitExportDeclaration(stmt as ExportDeclaration);
      case 'BreakStatement':
        return this.visitBreakStatement(stmt as BreakStatement);
      case 'ContinueStatement':
        return this.visitContinueStatement(stmt as ContinueStatement);
      case 'ThrowStatement':
        return this.visitThrowStatement(stmt as ThrowStatement);
      default:
        throw new Error(`Unknown statement type: ${(stmt as Statement).type}`);
    }
  }

  /**
   * Visit expression statement
   */
  private visitExpressionStmt(stmt: ExpressionStmt): string {
    return `${this.indent()}${this.visitExpression(stmt.expression)};`;
  }

  /**
   * Visit variable declaration
   * croissant -> const, baguette -> let
   */
  private visitVariableDeclaration(stmt: VariableDeclaration): string {
    const keyword = stmt.kind === 'croissant' ? 'const' : 'let';
    const name = stmt.name.lexeme;

    if (stmt.initializer) {
      const init = this.visitExpression(stmt.initializer);
      return `${this.indent()}${keyword} ${name} = ${init};`;
    }

    return `${this.indent()}${keyword} ${name};`;
  }

  /**
   * Visit function declaration
   * fonction -> function, asynchrone fonction -> async function
   */
  private visitFunctionDeclaration(stmt: FunctionDeclaration): string {
    const asyncKeyword = stmt.isAsync ? 'async ' : '';
    const name = stmt.name.lexeme;
    const params = stmt.params.map(p => p.lexeme).join(', ');

    const lines: string[] = [];
    lines.push(`${this.indent()}${asyncKeyword}function ${name}(${params}) {`);

    this.increaseIndent();
    for (const bodyStmt of stmt.body) {
      lines.push(this.visitStatement(bodyStmt));
    }
    this.decreaseIndent();

    lines.push(`${this.indent()}}`);
    return lines.join('\n');
  }

  /**
   * Visit if statement
   * franchement -> if, sinon -> else
   */
  private visitIfStatement(stmt: IfStatement): string {
    const condition = this.visitExpression(stmt.condition);
    const lines: string[] = [];

    lines.push(`${this.indent()}if (${condition}) {`);

    this.increaseIndent();
    if (stmt.thenBranch.type === 'BlockStatement') {
      const block = stmt.thenBranch as BlockStatement;
      for (const s of block.statements) {
        lines.push(this.visitStatement(s));
      }
    } else {
      lines.push(this.visitStatement(stmt.thenBranch));
    }
    this.decreaseIndent();

    if (stmt.elseBranch) {
      if (stmt.elseBranch.type === 'IfStatement') {
        // else if case
        lines.push(`${this.indent()}} else ${this.visitIfStatement(stmt.elseBranch as IfStatement).trimStart()}`);
        return lines.join('\n');
      } else {
        lines.push(`${this.indent()}} else {`);

        this.increaseIndent();
        if (stmt.elseBranch.type === 'BlockStatement') {
          const block = stmt.elseBranch as BlockStatement;
          for (const s of block.statements) {
            lines.push(this.visitStatement(s));
          }
        } else {
          lines.push(this.visitStatement(stmt.elseBranch));
        }
        this.decreaseIndent();

        lines.push(`${this.indent()}}`);
      }
    } else {
      lines.push(`${this.indent()}}`);
    }

    return lines.join('\n');
  }

  /**
   * Visit while statement
   * pendant -> while
   */
  private visitWhileStatement(stmt: WhileStatement): string {
    const condition = this.visitExpression(stmt.condition);
    const lines: string[] = [];

    lines.push(`${this.indent()}while (${condition}) {`);

    this.increaseIndent();
    if (stmt.body.type === 'BlockStatement') {
      const block = stmt.body as BlockStatement;
      for (const s of block.statements) {
        lines.push(this.visitStatement(s));
      }
    } else {
      lines.push(this.visitStatement(stmt.body));
    }
    this.decreaseIndent();

    lines.push(`${this.indent()}}`);
    return lines.join('\n');
  }

  /**
   * Visit for statement
   * pour -> for
   */
  private visitForStatement(stmt: ForStatement): string {
    let init = '';
    if (stmt.initializer) {
      // Remove the trailing semicolon and indentation from variable declaration
      init = this.visitStatement(stmt.initializer).trim().replace(/;$/, '');
    }

    const condition = stmt.condition ? this.visitExpression(stmt.condition) : '';
    const increment = stmt.increment ? this.visitExpression(stmt.increment) : '';

    const lines: string[] = [];
    lines.push(`${this.indent()}for (${init}; ${condition}; ${increment}) {`);

    this.increaseIndent();
    if (stmt.body.type === 'BlockStatement') {
      const block = stmt.body as BlockStatement;
      for (const s of block.statements) {
        lines.push(this.visitStatement(s));
      }
    } else {
      lines.push(this.visitStatement(stmt.body));
    }
    this.decreaseIndent();

    lines.push(`${this.indent()}}`);
    return lines.join('\n');
  }

  /**
   * Visit return statement
   * retourner -> return
   */
  private visitReturnStatement(stmt: ReturnStatement): string {
    if (stmt.value) {
      return `${this.indent()}return ${this.visitExpression(stmt.value)};`;
    }
    return `${this.indent()}return;`;
  }

  /**
   * Visit block statement
   */
  private visitBlockStatement(stmt: BlockStatement): string {
    const lines: string[] = [];
    lines.push(`${this.indent()}{`);

    this.increaseIndent();
    for (const s of stmt.statements) {
      lines.push(this.visitStatement(s));
    }
    this.decreaseIndent();

    lines.push(`${this.indent()}}`);
    return lines.join('\n');
  }

  /**
   * Visit try-catch-finally statement
   * malheureusement -> try, merde -> catch, quandmeme -> finally
   */
  private visitTryCatchStatement(stmt: TryCatchStatement): string {
    const lines: string[] = [];

    // Try block
    lines.push(`${this.indent()}try {`);
    this.increaseIndent();
    for (const s of stmt.tryBlock.statements) {
      lines.push(this.visitStatement(s));
    }
    this.decreaseIndent();

    // Catch block
    if (stmt.catchBlock) {
      const catchParam = stmt.catchParam ? stmt.catchParam.lexeme : 'e';
      lines.push(`${this.indent()}} catch (${catchParam}) {`);
      this.increaseIndent();
      for (const s of stmt.catchBlock.statements) {
        lines.push(this.visitStatement(s));
      }
      this.decreaseIndent();
    }

    // Finally block
    if (stmt.finallyBlock) {
      if (!stmt.catchBlock) {
        lines.push(`${this.indent()}}`);
      }
      lines.push(`${this.indent()}} finally {`);
      this.increaseIndent();
      for (const s of stmt.finallyBlock.statements) {
        lines.push(this.visitStatement(s));
      }
      this.decreaseIndent();
    }

    lines.push(`${this.indent()}}`);
    return lines.join('\n');
  }

  /**
   * Visit class declaration
   * classe -> class
   */
  private visitClassDeclaration(stmt: ClassDeclaration): string {
    const name = stmt.name.lexeme;
    const lines: string[] = [];

    if (stmt.superclass) {
      lines.push(`${this.indent()}class ${name} extends ${stmt.superclass.name} {`);
    } else {
      lines.push(`${this.indent()}class ${name} {`);
    }

    this.increaseIndent();
    for (const method of stmt.methods) {
      const asyncKeyword = method.isAsync ? 'async ' : '';
      const methodName = method.name.lexeme;
      const params = method.params.map(p => p.lexeme).join(', ');

      lines.push(`${this.indent()}${asyncKeyword}${methodName}(${params}) {`);
      this.increaseIndent();
      for (const bodyStmt of method.body) {
        lines.push(this.visitStatement(bodyStmt));
      }
      this.decreaseIndent();
      lines.push(`${this.indent()}}`);
    }
    this.decreaseIndent();

    lines.push(`${this.indent()}}`);
    return lines.join('\n');
  }

  /**
   * Visit import declaration
   * importer -> import, de -> from
   */
  private visitImportDeclaration(stmt: ImportDeclaration): string {
    const specifiers = stmt.specifiers.map(spec => {
      if (spec.imported.lexeme === spec.local.lexeme) {
        return spec.imported.lexeme;
      }
      return `${spec.imported.lexeme} as ${spec.local.lexeme}`;
    }).join(', ');

    const source = stmt.source.literal as string;
    return `${this.indent()}import { ${specifiers} } from "${source}";`;
  }

  /**
   * Visit export declaration
   * exporter -> export
   */
  private visitExportDeclaration(stmt: ExportDeclaration): string {
    if (stmt.declaration) {
      const decl = this.visitStatement(stmt.declaration).trimStart();
      return `${this.indent()}export ${decl}`;
    }

    const specifiers = stmt.specifiers.map(spec => {
      if (spec.local.lexeme === spec.exported.lexeme) {
        return spec.local.lexeme;
      }
      return `${spec.local.lexeme} as ${spec.exported.lexeme}`;
    }).join(', ');

    return `${this.indent()}export { ${specifiers} };`;
  }

  /**
   * Visit break statement
   */
  private visitBreakStatement(_stmt: BreakStatement): string {
    return `${this.indent()}break;`;
  }

  /**
   * Visit continue statement
   */
  private visitContinueStatement(_stmt: ContinueStatement): string {
    return `${this.indent()}continue;`;
  }

  /**
   * Visit throw statement
   */
  private visitThrowStatement(stmt: ThrowStatement): string {
    return `${this.indent()}throw ${this.visitExpression(stmt.argument)};`;
  }

  // ============================================================================
  // Expression Visitors
  // ============================================================================

  /**
   * Visit any expression node and dispatch to the appropriate visitor
   */
  private visitExpression(expr: Expression): string {
    switch (expr.type) {
      case 'BinaryExpr':
        return this.visitBinaryExpr(expr as BinaryExpr);
      case 'UnaryExpr':
        return this.visitUnaryExpr(expr as UnaryExpr);
      case 'LiteralExpr':
        return this.visitLiteralExpr(expr as LiteralExpr);
      case 'IdentifierExpr':
        return this.visitIdentifierExpr(expr as IdentifierExpr);
      case 'CallExpr':
        return this.visitCallExpr(expr as CallExpr);
      case 'AssignmentExpr':
        return this.visitAssignmentExpr(expr as AssignmentExpr);
      case 'MemberAssignmentExpr':
        return this.visitMemberAssignmentExpr(expr as MemberAssignmentExpr);
      case 'LogicalExpr':
        return this.visitLogicalExpr(expr as LogicalExpr);
      case 'GroupingExpr':
        return this.visitGroupingExpr(expr as GroupingExpr);
      case 'MemberExpr':
        return this.visitMemberExpr(expr as MemberExpr);
      case 'ArrayExpr':
        return this.visitArrayExpr(expr as ArrayExpr);
      case 'ObjectExpr':
        return this.visitObjectExpr(expr as ObjectExpr);
      case 'ThisExpr':
        return this.visitThisExpr(expr as ThisExpr);
      case 'NewExpr':
        return this.visitNewExpr(expr as NewExpr);
      case 'AwaitExpr':
        return this.visitAwaitExpr(expr as AwaitExpr);
      default:
        throw new Error(`Unknown expression type: ${(expr as Expression).type}`);
    }
  }

  /**
   * Visit binary expression
   * Handle et -> &&, ou -> ||, est -> ===, nest_pas -> !==
   */
  private visitBinaryExpr(expr: BinaryExpr): string {
    const left = this.visitExpression(expr.left);
    const right = this.visitExpression(expr.right);
    const op = this.translateOperator(expr.operator.type);

    return `${left} ${op} ${right}`;
  }

  /**
   * Visit unary expression
   * Handle pas -> !
   */
  private visitUnaryExpr(expr: UnaryExpr): string {
    const operand = this.visitExpression(expr.operand);
    const op = this.translateOperator(expr.operator.type);

    // No space between operator and operand for unary operators
    return `${op}${operand}`;
  }

  /**
   * Visit literal expression
   * Handle oui -> true, non -> false, rien -> null
   */
  private visitLiteralExpr(expr: LiteralExpr): string {
    if (expr.value === null) {
      return 'null';
    }
    if (typeof expr.value === 'string') {
      // Properly escape the string
      const escaped = expr.value
        .replace(/\\/g, '\\\\')
        .replace(/"/g, '\\"')
        .replace(/\n/g, '\\n')
        .replace(/\t/g, '\\t')
        .replace(/\r/g, '\\r');
      return `"${escaped}"`;
    }
    if (typeof expr.value === 'boolean') {
      return expr.value ? 'true' : 'false';
    }
    return String(expr.value);
  }

  /**
   * Visit identifier expression
   */
  private visitIdentifierExpr(expr: IdentifierExpr): string {
    return expr.name;
  }

  /**
   * Visit call expression
   * Handle built-in function transforms: dire() -> console.log()
   * Handle XION function transforms: connecter() -> xionStdlib.connecter()
   */
  private visitCallExpr(expr: CallExpr): string {
    let callee = this.visitExpression(expr.callee);

    // Check if this is a built-in function or XION function
    if (expr.callee.type === 'IdentifierExpr') {
      const name = (expr.callee as IdentifierExpr).name;
      if (BUILTIN_FUNCTIONS[name]) {
        callee = BUILTIN_FUNCTIONS[name];
      } else if (XION_FUNCTIONS[name]) {
        callee = XION_FUNCTIONS[name];
        this.usesXion = true; // Mark that XION functions are used
      }
    }

    const args = expr.args.map(arg => this.visitExpression(arg)).join(', ');
    return `${callee}(${args})`;
  }

  /**
   * Visit assignment expression
   */
  private visitAssignmentExpr(expr: AssignmentExpr): string {
    const name = expr.name.lexeme;
    const value = this.visitExpression(expr.value);
    return `${name} = ${value}`;
  }

  /**
   * Visit member assignment expression (obj.prop = value, arr[i] = value)
   */
  private visitMemberAssignmentExpr(expr: MemberAssignmentExpr): string {
    const object = this.visitExpression(expr.object);
    const value = this.visitExpression(expr.value);

    if (expr.computed) {
      const property = this.visitExpression(expr.property);
      return `${object}[${property}] = ${value}`;
    }

    const property = this.visitExpression(expr.property);
    return `${object}.${property} = ${value}`;
  }

  /**
   * Visit logical expression
   * et -> &&, ou -> ||
   */
  private visitLogicalExpr(expr: LogicalExpr): string {
    const left = this.visitExpression(expr.left);
    const right = this.visitExpression(expr.right);
    const op = this.translateOperator(expr.operator.type);

    return `${left} ${op} ${right}`;
  }

  /**
   * Visit grouping expression
   */
  private visitGroupingExpr(expr: GroupingExpr): string {
    return `(${this.visitExpression(expr.expression)})`;
  }

  /**
   * Visit member expression
   * obj.property or obj[expr]
   */
  private visitMemberExpr(expr: MemberExpr): string {
    const object = this.visitExpression(expr.object);

    if (expr.computed) {
      const property = this.visitExpression(expr.property);
      return `${object}[${property}]`;
    }

    const property = this.visitExpression(expr.property);
    return `${object}.${property}`;
  }

  /**
   * Visit array expression
   */
  private visitArrayExpr(expr: ArrayExpr): string {
    const elements = expr.elements.map(el => this.visitExpression(el)).join(', ');
    return `[${elements}]`;
  }

  /**
   * Visit object expression
   */
  private visitObjectExpr(expr: ObjectExpr): string {
    if (expr.properties.length === 0) {
      return '{}';
    }

    const props = expr.properties.map(prop => {
      let key: string;
      if (prop.computed) {
        // Computed property: [expr]
        key = `[${this.visitExpression({ type: 'IdentifierExpr', name: prop.key.lexeme, line: prop.key.line } as IdentifierExpr)}]`;
      } else {
        // Static property key - need to quote if it contains special characters or is a number
        const keyStr = prop.key.lexeme;
        // Check if key is a valid JS identifier (alphanumeric starting with letter/underscore)
        const isValidIdentifier = /^[a-zA-Z_$][a-zA-Z0-9_$]*$/.test(keyStr);
        // Also check if it's a pure number (which should be unquoted in JS)
        const isNumber = /^\d+$/.test(keyStr);

        if (isValidIdentifier || isNumber) {
          key = keyStr;
        } else {
          // Quote the key - escape any quotes within
          const escaped = keyStr.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
          key = `"${escaped}"`;
        }
      }
      const value = this.visitExpression(prop.value);
      return `${key}: ${value}`;
    }).join(', ');

    return `{ ${props} }`;
  }

  /**
   * Visit this expression
   * ceci -> this
   */
  private visitThisExpr(_expr: ThisExpr): string {
    return 'this';
  }

  /**
   * Visit new expression
   * nouveau -> new
   */
  private visitNewExpr(expr: NewExpr): string {
    const callee = this.visitExpression(expr.callee);
    const args = expr.args.map(arg => this.visitExpression(arg)).join(', ');
    return `new ${callee}(${args})`;
  }

  /**
   * Visit await expression
   * attendre -> await
   */
  private visitAwaitExpr(expr: AwaitExpr): string {
    return `await ${this.visitExpression(expr.argument)}`;
  }

  // ============================================================================
  // Helper Methods
  // ============================================================================

  /**
   * Translate Jertscript operators to JavaScript operators
   */
  private translateOperator(tokenType: TokenType): string {
    switch (tokenType) {
      // Logical operators
      case TokenType.ET:
        return '&&';
      case TokenType.OU:
        return '||';
      case TokenType.PAS:
        return '!';
      case TokenType.EST:
        return '===';
      case TokenType.NEST_PAS:
        return '!==';

      // Arithmetic operators
      case TokenType.PLUS:
        return '+';
      case TokenType.MINUS:
        return '-';
      case TokenType.STAR:
        return '*';
      case TokenType.SLASH:
        return '/';

      // Comparison operators
      case TokenType.LESS:
        return '<';
      case TokenType.GREATER:
        return '>';
      case TokenType.LESS_EQUAL:
        return '<=';
      case TokenType.GREATER_EQUAL:
        return '>=';
      case TokenType.EQUAL_EQUAL:
        return '==';
      case TokenType.EQUAL:
        return '=';

      default:
        throw new Error(`Unknown operator token type: ${tokenType}`);
    }
  }
}

/**
 * Convenience function to generate JavaScript from an AST
 */
export function generate(ast: Program): string {
  const generator = new Generator(ast);
  return generator.generate();
}

// Re-export AST types for convenience
export * from '../parser/ast';
